import { useCallback, useMemo, type ReactNode } from "react";
import { useSearchParams } from "react-router";
import { toast } from "@carbon6/c6-frontend-design";
import { GnbMenu } from "@/app/shell/Gnb";
import { PageBase, type PageBaseProps } from "@/app/shell/PageBase";
import { acceptCandidate, dismissCandidate } from "../api/candidateApi";
import type { Candidate } from "../domain/candidate";
import type { Dataset } from "../domain/dataset";
import {
  readCandidateFilters,
  writeCandidateFilters,
  type CandidateFilters,
} from "../feature/candidateFilters";
import { resolveDatasetErrorMessage } from "../feature/datasetFormatters";
import { CandidateTableSource } from "./CandidateTableSource";
import { CandidateFilterBar } from "./components/CandidateFilterBar";
import { CandidateListBody } from "./components/CandidateListBody";
import { CandidateRowActions } from "./components/CandidateRowActions";
import { DismissCandidateModal } from "./components/DismissCandidateModal";
import { openDatasetDetailWindow } from "./datasetDetailWindow";

export function CandidateListPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const filters = useMemo(() => readCandidateFilters(searchParams), [searchParams]);
  const onFiltersChange = useCallback(
    (next: CandidateFilters) =>
      setSearchParams((prev) => writeCandidateFilters(prev, next), { replace: true }),
    [setSearchParams],
  );
  const onOpenDataset = useCallback((dataset: Dataset) => {
    openDatasetDetailWindow({ datasetUuid: dataset.uuid, displayName: dataset.displayName });
  }, []);
  return (
    <CandidateListPageView
      bodyFlushTop
      currentMenu={GnbMenu.ScannedResults}
      filters={filters}
      pageTitle="Scanned Results"
      onFiltersChange={onFiltersChange}
      onOpenDataset={onOpenDataset}
    />
  );
}

type CandidateListPageViewProps = PageBaseProps & {
  filters: CandidateFilters;
  onFiltersChange: (filters: CandidateFilters) => void;
  onOpenDataset: (dataset: Dataset) => void;
};

type CandidateListPageViewState = {
  dismissTarget: Candidate | null;
  dismissReason: string;
  dismissErrorMessage: string | null;
  dismissSubmitting: boolean;
  decidingUuid: string | null;
};

class CandidateListPageView extends PageBase<
  CandidateListPageViewProps,
  CandidateListPageViewState
> {
  private readonly source = new CandidateTableSource({
    renderActions: (candidate) => this.renderRowActions(candidate),
    resolveErrorMessage: resolveDatasetErrorMessage,
  });

  state: CandidateListPageViewState = {
    dismissTarget: null,
    dismissReason: "",
    dismissErrorMessage: null,
    dismissSubmitting: false,
    decidingUuid: null,
  };

  protected override async initPage(): Promise<void> {
    await this.source.applyFilters(this.props.filters);
  }

  protected override disposePage(): void {
    this.source.dispose();
  }

  override componentDidUpdate(previous: CandidateListPageViewProps): void {
    if (JSON.stringify(previous.filters) !== JSON.stringify(this.props.filters)) {
      void this.source.applyFilters(this.props.filters);
    }
  }

  protected override renderBelowTabs(): ReactNode {
    return (
      <CandidateFilterBar
        filters={this.props.filters}
        source={this.source}
        onChange={this.props.onFiltersChange}
      />
    );
  }

  protected override renderContent(): ReactNode {
    const { dismissTarget, dismissReason, dismissErrorMessage, dismissSubmitting } = this.state;
    return (
      <>
        <CandidateListBody
          filtered={
            this.props.filters.search !== "" ||
            this.props.filters.formats.length > 0 ||
            this.props.filters.rootCodes.length > 0 ||
            this.props.filters.parentFolders.length > 0
          }
          source={this.source}
          onRefresh={this.handleRefresh}
        />
        {dismissTarget === null ? null : (
          <DismissCandidateModal
            errorMessage={dismissErrorMessage}
            reason={dismissReason}
            sourceRef={dismissTarget.sourceRef}
            submitting={dismissSubmitting}
            onCancel={this.closeDismissDialog}
            onConfirm={this.handleDismissConfirm}
            onReasonChange={this.handleDismissReasonChange}
          />
        )}
      </>
    );
  }

  private renderRowActions(candidate: Candidate): ReactNode {
    return (
      <CandidateRowActions
        busy={this.state.decidingUuid === candidate.uuid}
        candidate={candidate}
        onAccept={this.handleAcceptCandidate}
        onDismiss={this.openDismissDialog}
      />
    );
  }

  private readonly handleRefresh = (): void => {
    void this.source.reload();
  };

  private readonly handleAcceptCandidate = (candidate: Candidate): void => {
    if (this.state.decidingUuid !== null) return;
    this.setState({ decidingUuid: candidate.uuid });
    void acceptCandidate(candidate.uuid)
      .then(async (outcome) => {
        if (outcome.created) toast.success("후보를 Dataset 으로 등록했습니다.");
        else toast.info("이미 등록된 원본입니다. 기존 Dataset 을 엽니다.");
        this.setState({ decidingUuid: null });
        await this.source.removeCandidate(candidate.uuid);
        this.props.onOpenDataset(outcome.dataset);
      })
      .catch((error: unknown) => {
        this.setState({ decidingUuid: null });
        toast.error(resolveDatasetErrorMessage(error));
      });
  };

  private readonly openDismissDialog = (candidate: Candidate): void => {
    this.setState({
      dismissTarget: candidate,
      dismissReason: "",
      dismissErrorMessage: null,
      dismissSubmitting: false,
    });
  };

  private readonly closeDismissDialog = (): void => {
    this.setState({
      dismissTarget: null,
      dismissReason: "",
      dismissErrorMessage: null,
      dismissSubmitting: false,
    });
  };

  private readonly handleDismissReasonChange = (dismissReason: string): void => {
    this.setState({ dismissReason });
  };

  private readonly handleDismissConfirm = (): void => {
    const target = this.state.dismissTarget;
    if (target === null || this.state.dismissSubmitting) return;
    this.setState({ dismissSubmitting: true, dismissErrorMessage: null });
    const reason = this.state.dismissReason.trim();
    void dismissCandidate(target.uuid, reason === "" ? undefined : reason)
      .then(async () => {
        toast.success("후보를 폐기했습니다. 다시 제안되지 않습니다.");
        this.closeDismissDialog();
        await this.source.removeCandidate(target.uuid);
      })
      .catch((error: unknown) => {
        this.setState({
          dismissSubmitting: false,
          dismissErrorMessage: resolveDatasetErrorMessage(error),
        });
      });
  };
}
